import { Button, Modal, Text } from "@vericus/cadmus-ui";

import { create, useModal } from "@ebay/nice-modal-react";

import { ProjectIssueWithChildren } from "../../types";
import {
  cancelButton,
  deleteModalActions,
  deleteModalContent,
  deleteModalDescription,
  deleteModalTitle,
} from "./delete-confirmation-modal.css";

/*
 * Props for the DeleteIssueModal component.
 */
interface DeleteIssueModalProps {
  /**
   * The project issue that is targeted to be deleted.
   */
  issue: ProjectIssueWithChildren;

  /**
   * Callback function to be called when the delete action is confirmed.
   * @param issue - The project issue that is targeted to be deleted.
   */
  onConfirmDelete: (issue: ProjectIssueWithChildren) => void;

  /**
   * Indicates whether the issue has child issues.
   */
  hasChildren: boolean;
}

export const DeleteIssueModal = create<DeleteIssueModalProps>(
  (props: DeleteIssueModalProps) => {
    const modal = useModal();
    const { issue, onConfirmDelete, hasChildren } = props;

    return (
      <Modal.Root open={modal.visible}>
        <DeleteIssueModalContent
          issue={issue}
          onClose={() => modal.remove()}
          onConfirmDelete={onConfirmDelete}
          hasChildren={hasChildren}
        />
      </Modal.Root>
    );
  }
);

interface DeleteModalContentProps extends DeleteIssueModalProps {
  onClose: () => void;
}

export function DeleteIssueModalContent(props: DeleteModalContentProps) {
  const { issue, onClose, onConfirmDelete, hasChildren } = props;

  const handleDelete = () => {
    onConfirmDelete(issue);
    onClose();
  };

  return (
    <Modal.Content withCloseButton={false} className={deleteModalContent}>
      <Modal.Title asChild className={deleteModalTitle}>
        <Text kind="headingFour">Delete Task?</Text>
      </Modal.Title>
      <Modal.Description className={deleteModalDescription}>
        <Text kind="bodyMd">
          Are you sure you want to delete &quot;{issue.title}&quot;?
          {hasChildren && (
            <>
              <br />
              This will also delete all child tasks.
            </>
          )}
        </Text>
      </Modal.Description>
      <div className={deleteModalActions}>
        <Button onClick={onClose} className={cancelButton}>
          Cancel
        </Button>
        <Button kind="error" onClick={handleDelete}>
          Delete
        </Button>
      </div>
    </Modal.Content>
  );
}

/**
 * Props for BulkDeleteConfirmationModal component
 */
interface BulkDeleteConfirmationModalProps {
  /**
   * The count of selected Issues
   */
  selectedCount: number;
  /**
   * Callback function to be called when the delete action is confirmed.
   * @param selectedIds
   */
  onConfirmDelete: (selectedIds: string[]) => void;
  /**
   * The set of selected Issue ids
   */
  selectedIds: string[];
}

export const BulkDeleteConfirmationModal =
  create<BulkDeleteConfirmationModalProps>(
    (props: BulkDeleteConfirmationModalProps) => {
      const modal = useModal();
      const { selectedCount, onConfirmDelete, selectedIds } = props;

      return (
        <Modal.Root open={modal.visible}>
          <BulkDeleteConfirmationModalContent
            selectedCount={selectedCount}
            onClose={() => modal.remove()}
            onConfirmDelete={onConfirmDelete}
            selectedIds={selectedIds}
          />
        </Modal.Root>
      );
    }
  );

interface BulkDeleteConfirmationModalContentProps
  extends BulkDeleteConfirmationModalProps {
  onClose: () => void;
}

export function BulkDeleteConfirmationModalContent(
  props: BulkDeleteConfirmationModalContentProps
) {
  const { selectedCount, onClose, onConfirmDelete, selectedIds } = props;

  const handleDelete = () => {
    onConfirmDelete(selectedIds);
    onClose();
  };

  return (
    <Modal.Content withCloseButton={false} className={deleteModalContent}>
      <Modal.Title asChild className={deleteModalTitle}>
        <Text kind="headingFour">Delete Multiple Issues?</Text>
      </Modal.Title>
      <Modal.Description className={deleteModalDescription}>
        <Text kind="bodyMd">
          Are you sure you want to delete {selectedCount}{" "}
          {selectedCount === 1 ? "issue" : "issues"}? This action cannot be
          undone.
        </Text>
      </Modal.Description>
      <div className={deleteModalActions}>
        <Button onClick={onClose} className={cancelButton}>
          Cancel
        </Button>
        <Button kind="error" onClick={handleDelete}>
          Delete
        </Button>
      </div>
    </Modal.Content>
  );
}
